import { type BrowserTask, compareVersions, publisherRelease } from "@ondit/publishing";
import { useCallback, useEffect, useRef, useState } from "react";

export function usePublisherExtension() {
	const [status, setStatus] = useState<"checking" | "connected" | "missing" | "outdated">("checking");
	const [version, setVersion] = useState("");
	const [device, setDevice] = useState("");
	const probe = useRef("");
	const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const check = useCallback(() => {
		probe.current = crypto.randomUUID();
		setStatus("checking");
		clearTimeout(timeout.current);
		window.postMessage({ source: "ondit-panel", type: "ping", request_id: probe.current }, location.origin);
		timeout.current = setTimeout(() => setStatus("missing"), 2000);
	}, []);
	useEffect(() => {
		const receive = (event: MessageEvent) => {
			if (
				event.source !== window ||
				event.origin !== location.origin ||
				event.data?.source !== "ondit-extension" ||
				event.data.type !== "pong" ||
				event.data.request_id !== probe.current
			)
				return;
			clearTimeout(timeout.current);
			setVersion(event.data.version ?? "0.2.0");
			setDevice(event.data.device_id ?? "");
			setStatus(
				event.data.protocol !== publisherRelease.protocol ||
					compareVersions(event.data.version ?? "0.2.0", publisherRelease.version) < 0
					? "outdated"
					: "connected",
			);
		};
		window.addEventListener("message", receive);
		window.addEventListener("focus", check);
		check();
		return () => {
			clearTimeout(timeout.current);
			window.removeEventListener("message", receive);
			window.removeEventListener("focus", check);
		};
	}, [check]);
	return { status, check, version, device };
}

/** An acknowledgement means the assistant opened this saved snapshot, never that a post is public. */
export function openPublisher(task: BrowserTask, locale: string, scheduledAt?: string): Promise<void> {
	return new Promise((resolve, reject) => {
		const request = crypto.randomUUID();
		const done = (error?: Error) => {
			clearTimeout(timeout);
			window.removeEventListener("message", receive);
			error ? reject(error) : resolve();
		};
		const receive = (event: MessageEvent) => {
			const m = event.data;
			if (
				event.source !== window ||
				event.origin !== location.origin ||
				m?.source !== "ondit-extension" ||
				m.request_id !== request ||
				m.task_id !== task.id
			)
				return;
			if (m.type === "accepted") done();
			if (m.type === "rejected") done(new Error("publisher_unavailable"));
		};
		const timeout = setTimeout(() => done(new Error("publisher_unavailable")), 8000);
		window.addEventListener("message", receive);
		window.postMessage(
			{ source: "ondit-panel", type: "handoff", request_id: request, locale, task, scheduled_at: scheduledAt },
			location.origin,
		);
	});
}
